import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { identifyUserByStaffPin } from "../../../api/user";
import { logoutUser } from "../../../actions/authentication";
import { isHandheldPinSession } from "../../../helpers/handheldPin";
import {
  staffIdleLockDisplayName,
  shouldApplyStaffIdleLock,
} from "../../../helpers/staffIdleLock";
import {
  normalizeStaffPin,
  staffPinError,
  STAFF_PIN_MAX_LEN,
} from "../../../helpers/staffPin";
import { needsStaffSetupPrompt } from "../../../helpers/staffName";
import useStaffIdleLock from "../../../hooks/useStaffIdleLock";
import "./styles.scss";

const StaffIdleLockGate = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const currentUser = useSelector((s) => s?.currentUser?.user || {});
  const enterprise = useSelector((s) => s?.currentUser?.enterprise || {});

  // Don't idle-lock while first-login name/PIN setup is still open.
  // The gun uses the handheld PIN pad for lock + logout instead.
  const setupPending = needsStaffSetupPrompt(currentUser);
  const onHandheld = isHandheldPinSession({
    pathname: location.pathname,
    search: location.search,
  });
  const enabled =
    shouldApplyStaffIdleLock(currentUser, enterprise) &&
    !setupPending &&
    !onHandheld;

  const { locked, unlock, clear } = useStaffIdleLock({
    user: currentUser,
    enterprise,
    enabled,
  });

  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!locked) {
      setPin("");
      setError("");
      setBusy(false);
    }
  }, [locked]);

  if (!locked) return null;

  const employeeName = staffIdleLockDisplayName(currentUser);

  const handleUnlock = async (e) => {
    e?.preventDefault?.();
    const formatErr = staffPinError(pin);
    if (formatErr) {
      setError(formatErr);
      return;
    }

    setBusy(true);
    setError("");
    const normalized = normalizeStaffPin(pin);

    try {
      // Prefer local match when the current user record already has the PIN.
      // Fall back to identify-by-pin and require this account's uid.
      const localPin = normalizeStaffPin(currentUser?.staffPin);
      if (localPin) {
        if (localPin === normalized) {
          unlock();
          return;
        }
        setError("Incorrect PIN.");
        setPin("");
        return;
      }

      const identified = await identifyUserByStaffPin(normalized);
      if (identified?.uid && identified.uid === currentUser.uid) {
        unlock();
        return;
      }
      setError("Incorrect PIN.");
      setPin("");
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Incorrect PIN.";
      if (status === 404 || /no team member/i.test(message)) {
        setError("Incorrect PIN.");
      } else {
        setError(message);
      }
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = () => {
    clear();
    dispatch(logoutUser());
  };

  return (
    <div
      className="staff-idle-lock"
      role="dialog"
      aria-modal="true"
      aria-labelledby="staff-idle-lock-name"
    >
      <div className="staff-idle-lock__card">
        <p className="staff-idle-lock__eyebrow">Session locked</p>
        <h1 id="staff-idle-lock-name" className="staff-idle-lock__name">
          {employeeName}
        </h1>
        <p className="staff-idle-lock__hint">
          Enter your Team Member Pin to unlock this account.
        </p>

        <form className="staff-idle-lock__form" onSubmit={handleUnlock}>
          <input
            className="staff-idle-lock__input"
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            value={pin}
            onChange={(e) =>
              setPin(normalizeStaffPin(e.target.value).slice(0, STAFF_PIN_MAX_LEN))
            }
            placeholder="••••"
            disabled={busy}
            aria-label="Team Member Pin"
          />
          {error ? (
            <p className="staff-idle-lock__error" role="alert">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            className="staff-idle-lock__unlock"
            disabled={busy}
          >
            {busy ? "Checking…" : "Unlock"}
          </button>
        </form>

        <button
          type="button"
          className="staff-idle-lock__logout"
          onClick={handleLogout}
          disabled={busy}
        >
          Log out
        </button>
      </div>
    </div>
  );
};

export default StaffIdleLockGate;
